import { PlatformLocation } from "@angular/common";
import { Component, HostListener, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { AlertController, NavController, NavParams } from "ionic-angular";
//import moment from "moment";
import * as moment from "moment-timezone";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import Activity from "../../../models/company-resources/activity";
import ActivityGridItem from "../../../models/company-resources/activity-grid";
import ContentWrapper from "../../../models/content-wrapper/content-wrapper";
import { MessagePatient } from "../../../models/messaging/channel";
import { Patient } from "../../../models/patient-chart/patient";
import ContactSearchResult from "../../../models/resource-management/contact-search";
import { PatientChartService } from "../../../providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";

interface ActivityTime {
  hour: number;
  minute: number;
}

@Component({
  selector: "activity-log-edit",
  templateUrl: "activity-log-edit.html",
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
})
export class ActivityLogEditPage implements OnInit {
  userSearchSubject: Subject<string> = new Subject();
  patientSearchSubject: Subject<string> = new Subject();
  searchedPatients: MessagePatient[] = [];
  activity: Activity;
  //
  activityLogForm: FormGroup;
  subscriptions: Subscription[] = [];
  hours = [];
  mins: number[] = [0, 15, 30, 45];
  patientRequiredActivity = [
    1, 6, 9, 10, 11, 13, 14, 16, 17, 19, 21, 25, 47, 49, 50, 52, 53, 54, 57, 63,
    65,
  ];
  patientRequired: boolean = true;
  dateOfActivityMin: any = "";
  dateOfActivityMax: any = "";

  durationString: string = "0 hr 0 mins";
  totalTimeString: string = "0 hr 0 mins";

  initTimeValue: ActivityTime = {
    hour: null,
    minute: null,
  };

  initStartTime: ActivityTime = this.initTimeValue;
  initEndTime: ActivityTime = this.initTimeValue;

  //
  activityTypeOptions = [];
  // patientOptions = [];
  userOptions: ContactSearchResult[] = [];
  activityTitle: string = "Add New Activity";

  //
  constructor(
    protected formBuilder: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private resourceManagementService: ResourceManagementService,
    //     private volunteerManagementService: VolunteerManagementServiceProvider,
    private patientChartService: PatientChartService,
    private location: PlatformLocation
  ) {
    const now = new Date();
    const today = {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
    };
    this.dateOfActivityMin = {
      year: now.getFullYear() - 80,
      month: now.getMonth() + 1,
      day: now.getDate(),
    };
    this.dateOfActivityMax = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    };

    this.activityLogForm = this.formBuilder.group({
      userId: [null, Validators.required],
      patientChartId: [null, Validators.required],
      activityType: [Validators.required],
      dateOfActivity: [today, Validators.required],
      holiday: [],
      onCall: [],
      startTime: [null, Validators.required],
      endTime: [null, Validators.required],
      travelHour: [null],
      travelMinute: [null],
      addAnotherActivity: [false],
      mileage: [null],
      timezone: moment.tz.guess(),
    });
  }

  initActivity(reset: boolean = false) {
    if (this.navParams.get("activityId") > 0 && !reset) {
      this.activity = this.navParams.get("activity");
      let startDate = moment
        .tz(this.activity.startTime, this.activity.timezone)
        .format("YYYY-MM-DD");
      let startHour = moment
        .tz(this.activity.startTime, this.activity.timezone)
        .format("HH");
      let startMinute = moment
        .tz(this.activity.startTime, this.activity.timezone)
        .format("mm");
      let endHour = moment
        .tz(this.activity.endTime, this.activity.timezone)
        .format("HH");
      let endMinute = moment
        .tz(this.activity.endTime, this.activity.timezone)
        .format("mm");
      let travelHours = moment
        .utc(this.activity.travelTime * 1000)
        .format("HH"); // travel time is in seconds.  * 1000 (ms)
      let travelMinutes = moment
        .utc(this.activity.travelTime * 1000)
        .format("mm"); // travel time is in seconds.  * 1000 (ms)
      this.activityTitle = "Edit Activity";
      let st = {
        hour: startHour,
        minute: startMinute,
      };

      let et = {
        hour: endHour,
        minute: endMinute,
      };

      this.mapFormFromActivity(
        this.activity,
        new Date(startDate),
        st,
        et,
        travelHours,
        travelMinutes
      );
    } else {
      this.activity = new Activity();
      let st = {
        hour: 0,
        minute: 0,
      };
      let startDate = moment
        .tz(new Date(), moment.tz.guess())
        .format("YYYY-MM-DD");
      this.mapFormFromActivity(
        this.activity,
        new Date(startDate),
        st,
        st,
        "0",
        "0"
      );
    }
  }

  @HostListener("window:popstate", ["$event"])
  dismissModal() {
    this.navCtrl.pop();
  }

  ngOnInit(): void {
    this.userSearchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.userOptions = [];
        } else if (x.length > 2) {
          this.searchUsers(x);
        }
      });

    this.patientSearchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.searchedPatients = [];
        } else if (x.length > 2) {
          this.searchPatients(x);
        }
      });

    this.resourceManagementService
      .getLookUpValues()
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.activityTypeOptions = response["ActivityType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
        },
        (err) => {
          console.log(`Error loading lookups: ${err}`);
        }
      );

    this.hours = Array.from(Array(24).keys()).map((x) => {
      return {
        display: x,
        value: x,
      };
    });

    this.subscriptions.push(
      this.activityLogForm.valueChanges.subscribe((value) => {
        console.log(value.activityType);
        if (value.activityType != null) {
          this.patientRequiredCheck();
        }
        sessionStorage.setItem("activityLogForm", JSON.stringify(value));
        this.updateTime();
      })
    );

    this.initActivity();
  }

  patientRequiredCheck() {
    this.patientRequired = this.patientRequiredActivity.includes(
      parseInt(this.activityLogForm.get("activityType").value)
    );
    console.log(this.patientRequired);
    if (this.patientRequired) {
      this.addPatientField();
    } else {
      this.removePatientField();
    }
  }

  addPatientField() {
    if (!this.activityLogForm.contains("patientChartId")) {
      this.activityLogForm.addControl(
        "patientChartId",
        this.formBuilder.control(null, Validators.required)
      );
    }
  }

  removePatientField() {
    if (this.activityLogForm.contains("patientChartId")) {
      this.activityLogForm.removeControl("patientChartId");
    }
  }

  changeTime($event, field) {
    if ($event != null && $event != "null") {
      let time: string = $event;
      this.activityLogForm.get(field).setValue(JSON.parse(time));
    }
  }

  updateTime() {
    let startTime = this.activityLogForm.get("startTime").value;
    let endTime = this.activityLogForm.get("endTime").value;
    const start = moment.duration({
      hour: startTime ? startTime.hour : 0,
      minute: startTime ? startTime.minute : 0,
    });
    const end = moment.duration({
      hour: endTime ? endTime.hour : 0,
      minute: endTime ? endTime.minute : 0,
    });
    const travel = moment.duration(this.travelTimeInSeconds, "seconds");

    let duration = end.subtract(start);

    if (!(duration.hours() >= 0 && duration.minutes() >= 0)) {
      let overNight = moment.duration({ hour: 24, minute: 0 });
      duration = duration.add(overNight);
    }

    this.durationString = moment
      .utc(duration.asMilliseconds())
      .format("H [hr] m [mins]");
    let total = travel.add(duration);
    this.totalTimeString = moment
      .utc(total.asMilliseconds())
      .format("H [hr] m [mins]");
    if (total.days()) {
      this.totalTimeString =
        24 +
        +this.totalTimeString.substring(0, 2) +
        this.totalTimeString.substring(2);
    }
  }

  /**
   //  * Handles the form submission
   //  */
  submit() {
    let alert = this.alertController.create({
      title: "Submit Confirmation",
      message: "Are you sure you wish to save this activity?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            //Service call to submit
            let obj: any = this.mapActivityFromForm(this.activityLogForm.value);
            console.log(this.activity);
            console.log(obj);
            if (this.activity.id > 0) {
              this.resourceManagementService
                .updateActivity(this.activity.id, obj)
                .pipe()
                .take(1)
                .subscribe(
                  (response) => {},
                  (err) => {},
                  () => {
                    this.navParams
                      .get("grid")
                      .loadCompanyResource(+this.navParams.get("page"));
                    sessionStorage.removeItem("activityLogForm");
                  }
                );
            } else {
              this.resourceManagementService
                .createActivityLogEntry(obj)
                .pipe()
                .take(1)
                .subscribe(
                  (response) => {},
                  (err) => {},
                  () => {
                    this.navParams
                      .get("grid")
                      .loadCompanyResource(+this.navParams.get("page"));
                    sessionStorage.removeItem("activityLogForm");
                  }
                );
            }

            if (this.activityLogForm.value.addAnotherActivity) {
              this.activityLogForm.reset({ emitEvent: true });
              this.searchedPatients = [];
              this.userOptions = [];
              this.initActivity(true);
              this.addPatientField();
              this.patientRequired = true;
            } else {
              this.navCtrl.pop();
              // sessionStorage.clear();
            }
          },
        },
      ],
    });
    alert.present();
  }

  mapFormFromActivity(
    activity: Activity,
    activityDate: Date,
    startTime: ActivityTime,
    endTime: ActivityTime,
    travelHours: string,
    travelMinutes: string
  ): void {
    this.activityLogForm.controls["activityType"].setValue(
      activity.activityType
    );
    this.activityLogForm.controls["mileage"].setValue(activity.mileage, {
      emitEvent: false,
    });
    this.activityLogForm.controls["holiday"].setValue(activity.holiday, {
      emitEvent: false,
    });
    this.activityLogForm.controls["onCall"].setValue(activity.onCall, {
      emitEvent: false,
    });
    this.initStartTime = startTime;
    this.initEndTime = endTime;

    let activityGridItem = new ActivityGridItem();
    let patient: Patient;

    if (this.navParams.get("activityGridItem")) {
      activityGridItem = this.navParams.get(
        "activityGridItem"
      ) as ActivityGridItem;
    }
    if (this.navParams.get("patient")) {
      patient = this.navParams.get("patient").patient as Patient;
    } else {
      patient = undefined;
    }

    if (!activity.id || activity.id < 1) {
      return;
    }

    this.activityLogForm.controls["dateOfActivity"].setValue(activityDate, {
      emitEvent: false,
    });
    this.activityLogForm.controls["startTime"].setValue(startTime, {
      emitEvent: false,
    });
    this.activityLogForm.controls["endTime"].setValue(endTime, {
      emitEvent: false,
    });
    this.activityLogForm.controls["travelHour"].setValue(
      parseInt(travelHours),
      { emitEvent: true }
    );
    this.activityLogForm.controls["travelMinute"].setValue(
      parseInt(travelMinutes),
      { emitEvent: true }
    );

    let userOption: ContactSearchResult = {
      firstName: activityGridItem.firstName,
      id: activity.contactId,
      lastName: activityGridItem.lastName,
      contactType: "0",
      get fullName(): string {
        return `${this.firstName} ${this.lastName} - ${this.id}`;
      },
    };

    if (patient) {
      let patientOption: MessagePatient = {
        firstName: patient.firstName,
        id: patient.id,
        lastName: patient.lastName,
        displayName: `${patient.firstName} ${patient.lastName} - ${patient.id}`,
        internalId: patient.id,
        typeOfCare: patient.careType,
      };

      if (this.patientRequired) {
        this.activityLogForm.controls["patientChartId"].setValue(
          patientOption.id,
          { emitEvent: false }
        );
      }

      this.searchedPatients.push(patientOption);
    }

    this.activityLogForm.controls["userId"].setValue(userOption.id, {
      emitEvent: false,
    });
    this.userOptions.push(userOption);
    this.updateTime();
  }

  mapActivityFromForm(form: any): Activity {
    let activity = new Activity();
    activity.contactId = form.userId;
    activity.patientChartId = form.patientChartId;
    activity.activityType = form.activityType;
    activity.timezone = form.timezone;
    // activity.date = moment.utc(form.dateOfActivity).format('YYYY-MM-DD');
    activity.holiday = form.holiday;
    activity.onCall = form.onCall;
    if (form.startTime !== null) {
      activity.startTime = new Date(
        moment
          .utc(form.dateOfActivity)
          .hour(form.startTime.hour)
          .minute(form.startTime.minute)
          .format()
      );
    }
    if (form.endTime !== null) {
      activity.endTime = new Date(
        moment
          .utc(form.dateOfActivity)
          .hour(form.endTime.hour)
          .minute(form.endTime.minute)
          .format()
      );
    }
    activity.travelTime = this.travelTimeInSeconds;
    // activity.travelTime = moment.utc(form.dateOfActivity).hour(form.travelHour === null ? 0: form.travelHour)
    //     .minute(form.travelMinute === null ? 0: form.travelMinute).format()
    activity.mileage = form.mileage;
    return activity;
  }

  // /**
  //  * Handles the cancel event
  //  */
  cancel() {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present();
  }

  searchPatientInputChanged(e) {
    let searchTerm = e.target.value;
    this.patientSearchSubject.next(searchTerm);
  }

  searchUserInputChanged(e) {
    let searchTerm = e.target.value;
    this.userSearchSubject.next(searchTerm);
  }

  searchPatients(search: string): void {
    console.log("search triggered");
    this.searchedPatients = [];
    this.getPatients(search);
  }

  searchUsers(search: string) {
    console.log("search users triggered");
    this.userOptions = [];
    this.resourceManagementService
      .getContactSearch(search)
      .pipe()
      .subscribe((x) => {
        this.userOptions = x;
      });
  }

  // region properties
  get travelHoursInSeconds(): number {
    let travelHour = this.activityLogForm.get("travelHour").value
      ? this.activityLogForm.get("travelHour").value
      : 0;
    return travelHour * 3600;
  }

  get travelMinutesInSeconds(): number {
    let travelMinute = this.activityLogForm.get("travelMinute").value
      ? this.activityLogForm.get("travelMinute").value
      : 0;
    return travelMinute * 60;
  }

  get travelTimeInSeconds(): number {
    return this.travelHoursInSeconds + this.travelMinutesInSeconds;
  }

  getPatients(search: string) {
    const options = ["On-Service", "Referral", "Evaluation"];

    options.forEach((option) => {
      this.patientChartService
        .getPatientsByParams(search, 0, 10, "lastName", "asc", option)
        .pipe()
        .subscribe((x: ContentWrapper<ChartGridItem>) => {
          let a = [];

          x.content.forEach((x) => {
            a.push({
              id: x.externalId,
              firstName: x.firstName,
              lastName: x.lastName,
              displayName: `${x.firstName} ${x.lastName} - ${x.externalId}`,
            });
          });

          this.searchedPatients.push(...a);
          this.searchedPatients = [...this.searchedPatients];
        });
    });
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/activity-log-edit/activity-log-edit.ts